import { DomainUserUtil } from './../shared/utils/domain-user-util';
import { Inject, Injectable, inject, DOCUMENT } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { LoginModel } from '../shared/login.model';
import { environment } from '../../../environments/environment';
import { Role, UserAccountClient, UserInfoDTO } from '../models/models.swagger.generated';
import { TranslateService } from '@ngx-translate/core';
import { LoginProxy } from '../http/login-proxy.service';
import { Idle } from '@ng-idle/core';

import { SyncfusionTranslationService } from './syncfusion-translation.service';
import { DateTime, Settings } from 'luxon';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { loginRoute } from 'src/app/core/routing/route-names';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // store the URL so we can redirect after logging in
  public get redirectUrl(): string {
    return this._redirectUrl;
  }
  public set redirectUrl(value: string) {
    this._redirectUrl = value;
  }
  private _redirectUrl: string;

  private userInfoDTO: UserInfoDTO;

  private isTokenRefreshing: boolean = false;
  private refreshTokenSubject: Observable<any>;

  constructor(
    private loginProxy: LoginProxy,
    private accountClient: UserAccountClient,
    private readonly translate: TranslateService,
    private idle: Idle,
    private router: Router,
    private readonly syncfusionTranslationService: SyncfusionTranslationService,
    private msalAuthService: MsalService,
    @Inject(DOCUMENT) private document: Document) {
    this.resetLocalStorageIfNecessary();
    // Reset user info from the local storage
    if (localStorage[environment.localStorageUserInfo]) {
      this.userInfoDTO = UserInfoDTO.fromJS(JSON.parse(localStorage[environment.localStorageUserInfo]));
      this.setClientLanguage(this.userInfoDTO.language);
      setTimeout(() => this.idle.watch(), 1000);
    }
  }

  public login(loginData: LoginModel): Observable<string> {
    return this.loginProxy.login(loginData).pipe(
      tap(([token, refreshToken, tokenExpiration]) => {
        // login successful if there's a jwt token in the response
        if (token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage[environment.localStorageToken] = token;
          localStorage[environment.localStorageTokenExpiration] = tokenExpiration.toMillis();
          // start idle service to watch
          this.idle.watch();
          this.updateActivityTimeStamp();
        }
        if (refreshToken) {
          localStorage[environment.localStorageRefreshToken] = refreshToken;
        }
      }),
      switchMap(() => {
        // get current user info to get the user's roles
        return this.accountClient.info().pipe(
          catchError((error) => {
            this.resetLocalStorage();
            throw error;
          })
        );
      }),
      map((userInfo: UserInfoDTO) => {
        this.setUserInfoDTO(userInfo);
        return localStorage[environment.localStorageToken];
      })
    );
  }

  public loginAfterAzureMFA(): Observable<string> {
    return this.loginProxy.loginWithAzureToken().pipe(
      tap(([token, refreshToken, tokenExpiration]) => {
        // login successful if there's a jwt token in the response
        if (token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage[environment.localStorageToken] = token;
          localStorage[environment.localStorageTokenExpiration] = tokenExpiration.toMillis();
          // start idle service to watch
          this.idle.watch();
          this.updateActivityTimeStamp();
        }
        if (refreshToken) {
          localStorage[environment.localStorageRefreshToken] = refreshToken;
        }
      }),
      switchMap(() => {
        // get current user info to get the user's roles
        return this.accountClient.info().pipe(
          catchError((error) => {
            this.resetLocalStorage();
            throw error;
          })
        );
      }),
      map((userInfo: UserInfoDTO) => {
        this.setUserInfoDTO(userInfo);
        return localStorage[environment.localStorageToken];
      })
    );
  }

  public logout(): void {
    this.msalLogout().subscribe(() => {
      this.bearerTokenLogout();
    });
  }

  public logoutOnKill(): void {
    this.bearerTokenLogout();
    this.msalLogout();
  }

  public setUserLanguage(language: string) {
    const userInfo = this.getUserInfo();
    if (userInfo == null) {
      this.setClientLanguage(language);
      return;
    }
    userInfo.language = language;
    this.setUserInfoDTO(userInfo);
    this.accountClient.setUserLanguage(language).subscribe();
  }

  public isUserLoggedIn(): boolean {
    if (localStorage[environment.localStorageToken]) {
      return true;
    } else {
      return false;
    }
  }

  public isUserAdmin(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin]);
  }

  public doesUserHaveAnyRole(roles: Role[]): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    if (!roles || roles.length === 0) {
      return false;
    }

    return this.userInfoDTO.roles.some(r => roles.includes(r));
  }

  public canUserAccessAnyAssets(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ProductAssetManagement, Role.BrandLogoManagement, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin, Role.AssetStorageDownload, Role.AssetStorageUpload, Role.AssetStorageGroupMember]);
  }

  public canUserAccessProductAssets(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ProductAssetManagement]);
  }

  public canUserAccessBrandLogos(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.BrandLogoManagement]);
  }

  public canUserAccessAssetStorage(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin, Role.AssetStorageDownload, Role.AssetStorageUpload, Role.AssetStorageGroupMember]);
  }

  public canUserAccessAssetCollections(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin]);
  }

  public canUserAccessAssetStorageSettings(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin]);
  }

  public canUserAccessExportConfiguration(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin, Role.ConfigurationInterfaceUser]);
  }

  public canUserAccessExportHistory(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin]);
  }

  public canUserAccessImportInterface(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin, Role.ConfigurationInterfaceUser]);
  }

  public canUserEditImportConfigurations(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin]);
  }

  public canUserEditExportConfigurations(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin]);
  }

  public isUserAdminOrConfigurationAdmin(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin]);
  }

  public isUserAdminOrAssetStorageAdmin(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin]);
  }

  public isUserAdminOrConfigurationAdminOrAssetAdmin(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin]);
  }

  public hasUserUserRetreivalRole(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.ConfigurationInterfaceAdmin, Role.ConfigurationInterfaceUser, Role.AssetStorageAdmin, Role.AssetStorageLimitedAdmin]);
  }

  public hasUserVendorScoreCardAdminRole(): boolean {
    return this.doesUserHaveAnyRole([Role.Admin, Role.VendorScoreCardFull]);
  }

  public isUserClientUser(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }
    return this.userInfoDTO.client ? true : false;
  }

  public isUserDomainUser(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }
    return DomainUserUtil.isDomainUser(this.userInfoDTO?.userName);
  }

  public getUserInfo(): UserInfoDTO {
    if (!this.isUserLoggedIn() || !localStorage[environment.localStorageUserInfo]) {
      return null;
    }

    if (!this.userInfoDTO) {
      this.userInfoDTO = UserInfoDTO.fromJS(JSON.parse(localStorage[environment.localStorageUserInfo]));
    }
    return this.userInfoDTO;
  }

  public loadUserInfoDTO(): void {
    this.accountClient.info().subscribe(userInfo => {
      this.setUserInfoDTO(userInfo);
    });
  }

  private setUserInfoDTO(userInfo: UserInfoDTO) {
    if (!userInfo) {
      return;
    }
    this.userInfoDTO = userInfo;
    localStorage[environment.localStorageUserInfo] = JSON.stringify(userInfo);
    this.setClientLanguage(userInfo.language);
  }

  public updateActivityTimeStamp(): void {
    if (this.isUserLoggedIn()) {
      localStorage[environment.localStorageActivityTimestamp] = Date.now;
    }
  }

  public getBearerToken(): string {
    return localStorage[environment.localStorageToken];
  }

  public refreshTokenIfNecessary(callbackUrl: string, redirectOnError: boolean): Observable<boolean> {
    const token = localStorage[environment.localStorageToken];
    const expirationInMillis = localStorage[environment.localStorageTokenExpiration];
    if (token && expirationInMillis && DateTime.fromMillis(Number(expirationInMillis)).diffNow().as('seconds') > 600) {
      return of(true);
    }
    if (this.isTokenRefreshing) {
      return this.refreshTokenSubject;
    } else {
      this.isTokenRefreshing = true;
      this.refreshTokenSubject = this.loginProxy.refreshTokenLogin(localStorage[environment.localStorageRefreshToken]).pipe(
        map(([token, refreshToken, tokenExpiration]) => {
          // login successful if there's a jwt token in the response
          if (token) {
            localStorage[environment.localStorageToken] = token;
            localStorage[environment.localStorageTokenExpiration] = tokenExpiration.toMillis();
          }
          if (refreshToken) {
            localStorage[environment.localStorageRefreshToken] = refreshToken;
          }
          this.isTokenRefreshing = false;
          return true;
        }),
        catchError((err: any) => {
          if (redirectOnError) {
            if (callbackUrl != null) {
              this.redirectUrl = callbackUrl;
            }
            localStorage.removeItem(environment.localStorageToken);
            localStorage.removeItem(environment.localStorageTokenExpiration);
            localStorage.removeItem(environment.localStorageRefreshToken);
            this.router.navigate([loginRoute]);
          }
          this.isTokenRefreshing = false;
          return of(false);
        }));
      return this.refreshTokenSubject;
    }
  }

  private setClientLanguage(language: string) {
    const supportedLanguages = environment.supportedLanguages?.split(',').filter(s => s?.length > 0);
    if (!supportedLanguages) {
      console.error('no supported languages defined!');
      return;
    }
    if (!language) {
      language = supportedLanguages[0];
      if (this.userInfoDTO) {
        this.userInfoDTO.language = language;
      }
    }
    Settings.defaultLocale = language;
    this.syncfusionTranslationService.use(language);
    this.translate.use(language);
    if (environment.allowBrowserTranslation) {
      this.document.documentElement.lang = language;
    } else {
      this.document.documentElement.translate = false;
    }
  }

  private resetLocalStorageIfNecessary() {
    if (environment.production) {
      const timestamp = localStorage[environment.localStorageActivityTimestamp];
      if (!timestamp || timestamp + (environment.idleTimeoutInSeconds * 1000) < Date.now) {
        this.resetLocalStorage();
      }
    }

    // avoid possible dead lock
    if ((localStorage[environment.localStorageUserInfo] && !localStorage[environment.localStorageToken]) || (!localStorage[environment.localStorageUserInfo] && localStorage[environment.localStorageToken])) {
      this.resetLocalStorage();
    }
  }

  private resetLocalStorage() {
    localStorage.removeItem(environment.localStorageToken);
    localStorage.removeItem(environment.localStorageTokenExpiration);
    localStorage.removeItem(environment.localStorageRefreshToken);
    localStorage.removeItem(environment.localStorageUserInfo);
  }

  private bearerTokenLogout(): void {
    var token = localStorage[environment.localStorageToken];
    if (!token) {
      return;
    }
    this.accountClient.logout().subscribe();
    setTimeout(() => {
      this.resetLocalStorage();
      this.redirectUrl = null;
      location.reload();
    }, 0);
  }

  private msalLogout(): Observable<void> {
    let activeAccount = this.msalAuthService.instance.getActiveAccount();
    if (activeAccount) {
      return this.msalAuthService.logoutPopup({account: activeAccount}).pipe(
        tap(() => {
          this.msalAuthService.instance.setActiveAccount(null);
          this.msalAuthService.instance.clearCache();
        }),
        catchError((error) => {
          return of(void 0);
        })
      );
    }
    return of(void 0);
  }
}

export const tokenRefreshResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(AuthService).refreshTokenIfNecessary(state.url, true);
};
